/**
 * Back In Stock Drawer Component
 * Zara-inspired drawer for displaying size availability and email notification signup
 */

if (!customElements.get('back-in-stock-drawer')) {
  class BackInStockDrawer extends HTMLElement {
    constructor() {
      super();

      // Element references
      this.overlay = this.querySelector('[data-bis-overlay]');
      this.closeButton = this.querySelector('[data-bis-close]');
      this.lengthContainer = this.querySelector('[data-length-container]');
      this.lengthList = this.querySelector('[data-length-list]');
      this.sizesContainer = this.querySelector('[data-sizes-container]');
      this.sizesList = this.querySelector('[data-sizes-list]');
      this.formContainer = this.querySelector('[data-form-container]');
      this.form = this.querySelector('[data-bis-form]');
      this.emailInput = this.querySelector('[data-bis-email]');
      this.submitButton = this.querySelector('[data-bis-submit]');
      this.submitSpinner = this.submitButton?.querySelector('.loading__spinner');
      this.backButton = this.querySelector('[data-back-to-sizes]');
      this.backToLengthButton = this.querySelector('[data-back-to-length]');
      this.selectedSizeDisplay = this.querySelector('[data-selected-size]');
      this.successMessage = this.querySelector('[data-success-message]');
      this.errorMessage = this.querySelector('[data-error-message]');
      this.errorText = this.querySelector('[data-error-text]');
      this.subheading = this.querySelector('[data-bis-subheading]');
      this.originalSubheadingText = this.subheading?.textContent?.trim() || '';

      // State
      this.activeElement = null;
      this.selectedVariantId = null;
      this.selectedSizeValue = null;
      this.selectedVariantSku = null;
      this.selectedVariantPrice = null;
      this.selectedLength = null;
      this.isSubmitting = false;

      // Product data from data attributes
      this.productId = this.dataset.productId;
      this.productTitle = this.dataset.productTitle;
      this.productUrl = this.dataset.productUrl;
      this.productImage = this.dataset.productImage;
      this.sizeOptionPosition = parseInt(this.dataset.sizeOptionPosition) || 1;
      this.lengthOptionPosition = parseInt(this.dataset.lengthOptionPosition) || null;
      this.colorOptionPosition = parseInt(this.dataset.colorOptionPosition) || null;
      this.currentColor = this.dataset.currentColor || null;
      this.currentLength = this.dataset.currentLength || null;
      this.hasLengthOption = !!this.lengthContainer;

      // Parse variants data
      const variantsScript = this.querySelector('[data-bis-variants]');
      this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];

      // Bind methods
      this.handleKeyUp = this.handleKeyUp.bind(this);
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
      this.handleLengthClick = this.handleLengthClick.bind(this);
      this.handleSizeClick = this.handleSizeClick.bind(this);
      this.handleBackClick = this.handleBackClick.bind(this);
      this.handleBackToLengthClick = this.handleBackToLengthClick.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);

      // Initialize events
      this.bindEvents();

      // Subscribe to variant change events
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );
      }
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
    }

    bindEvents() {
      this.addEventListener('keyup', this.handleKeyUp);
      this.overlay?.addEventListener('click', this.handleOverlayClick);
      this.closeButton?.addEventListener('click', () => this.close());
      this.backButton?.addEventListener('click', this.handleBackClick);
      this.backToLengthButton?.addEventListener('click', this.handleBackToLengthClick);
      this.form?.addEventListener('submit', this.handleFormSubmit);

      // Delegate length button clicks
      this.lengthList?.addEventListener('click', this.handleLengthClick);

      // Delegate size button clicks
      this.sizesList?.addEventListener('click', this.handleSizeClick);
    }

    // ----- OPEN/CLOSE METHODS -----

    open(triggeredBy = null) {
      if (triggeredBy) {
        this.activeElement = triggeredBy;
      }

      // Store original parent so we can restore on close
      if (!this._originalParent) {
        this._originalParent = this.parentElement;
        this._originalNextSibling = this.nextElementSibling;
      }

      // Move drawer to body to escape parent stacking contexts
      document.body.appendChild(this);

      // Reset to initial state
      this.resetToSizeSelection();

      // Update sizes based on current color selection
      this.updateSizesForCurrentColor();

      // Pre-select the current size from PDP
      this.preselectCurrentSize();

      // Animate in
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      // Trap focus after transition
      this.addEventListener('transitionend', () => {
        if (!this.classList.contains('active')) return;
        const focusTarget = this.querySelector('.bis-drawer__inner') || this.closeButton;
        if (typeof trapFocus === 'function') {
          trapFocus(this, focusTarget);
        }
      }, { once: true });

      // Prevent body scroll and signal drawer is open (for hiding sticky ATC)
      document.body.classList.add('overflow-hidden', 'bis-drawer-open');

      // Dispatch open event
      this.dispatchEvent(new CustomEvent('bis-drawer:open', { bubbles: true }));
    }

    close() {
      this.classList.remove('active');

      // Restore body scroll and remove drawer open signal
      document.body.classList.remove('overflow-hidden', 'bis-drawer-open');
      document.documentElement.classList.remove('overflow-hidden');

      // Release focus trap
      if (typeof removeTrapFocus === 'function') {
        try {
          removeTrapFocus(this.activeElement);
        } catch (e) {
          // ignore
        }
      }

      // Dispatch close event
      this.dispatchEvent(new CustomEvent('bis-drawer:close', { bubbles: true }));
    }

    // ----- EVENT HANDLERS -----

    handleKeyUp(event) {
      if (event.code === 'Escape') {
        this.close();
      }
    }

    handleOverlayClick() {
      this.close();
    }

    handleSizeClick(event) {
      const button = event.target.closest('.bis-drawer__size-button');
      if (!button) return;

      const sizeItem = button.closest('.bis-drawer__size-item');
      const variantId = sizeItem.dataset.variantId;
      const sizeValue = sizeItem.dataset.sizeValue;
      const variantSku = sizeItem.dataset.variantSku || '';
      const variantPrice = sizeItem.dataset.variantPrice || '0';
      const isAvailable = sizeItem.dataset.available === 'true';
      const action = button.dataset.action;

      // Update visual selection
      this.updateSelectedSize(sizeItem);

      if (action === 'select-size' && isAvailable) {
        // Available size - close drawer and select variant
        this.selectAvailableVariant(variantId);
      } else if (action === 'notify-me' && !isAvailable) {
        // Unavailable size - show email form
        this.showEmailForm(variantId, sizeValue, variantSku, variantPrice);
      }
    }

    handleLengthClick(event) {
      const button = event.target.closest('.bis-drawer__size-button');
      if (!button || button.dataset.action !== 'select-length') return;

      const lengthItem = button.closest('.bis-drawer__size-item');
      const lengthValue = lengthItem.dataset.lengthValue;

      // Update visual selection on length list
      const lengthItems = this.lengthList?.querySelectorAll('.bis-drawer__size-item');
      lengthItems?.forEach(item => item.classList.remove('bis-drawer__size-item--selected'));
      lengthItem.classList.add('bis-drawer__size-item--selected');

      // Store selected length
      this.selectedLength = lengthValue;

      // Hide length step, show sizes step
      this.lengthContainer?.setAttribute('hidden', '');
      this.sizesContainer?.removeAttribute('hidden');

      // Update sizes filtered by colour + length
      this.updateSizesForCurrentSelection();
    }

    handleBackToLengthClick() {
      // Hide sizes, show length
      this.sizesContainer?.setAttribute('hidden', '');
      this.lengthContainer?.removeAttribute('hidden');
      this.selectedLength = null;
    }

    handleBackClick() {
      this.resetToSizeSelection();
    }

    async handleFormSubmit(event) {
      event.preventDefault();

      if (this.isSubmitting) return;

      const email = this.emailInput?.value?.trim();

      // Validation
      if (!email || !this.validateEmail(email)) {
        this.showError('Please enter a valid email address.');
        return;
      }

      if (!this.selectedVariantId) {
        this.showError('No variant selected. Please try again.');
        return;
      }

      // Submit to SWYM
      await this.submitToSwym(email);
    }

    handleVariantChange(event) {
      const data = event?.data || event;
      if (!data) return;

      // Update current color if color option changed
      if (this.colorOptionPosition && data.variant) {
        const newColor = data.variant.options[this.colorOptionPosition - 1];
        if (newColor && newColor !== this.currentColor) {
          this.currentColor = newColor;
          this.dataset.currentColor = newColor;

          // If drawer is open, update sizes
          if (this.classList.contains('active')) {
            this.updateSizesForCurrentColor();
          }
        }
      }
    }

    // ----- UI STATE METHODS -----

    resetToSizeSelection() {
      // Restore original header text
      if (this.subheading && this.originalSubheadingText) {
        this.subheading.textContent = this.originalSubheadingText;
      }

      // Hide form
      this.formContainer?.setAttribute('hidden', '');

      // If product has length option, go back to length step
      if (this.hasLengthOption) {
        this.sizesContainer?.setAttribute('hidden', '');
        this.lengthContainer?.removeAttribute('hidden');
        this.selectedLength = null;
      } else {
        this.sizesContainer?.removeAttribute('hidden');
      }

      // Reset form state
      this.successMessage?.setAttribute('hidden', '');
      this.errorMessage?.setAttribute('hidden', '');
      this.form?.removeAttribute('hidden');
      this.form?.reset();

      // Clear selection
      this.selectedVariantId = null;
      this.selectedSizeValue = null;
      this.selectedVariantSku = null;
      this.selectedVariantPrice = null;

      // Reset submit button
      this.submitButton?.classList.remove('loading');
      this.submitButton?.removeAttribute('disabled');
      this.submitSpinner?.classList.add('hidden');
      this.isSubmitting = false;
    }

    showEmailForm(variantId, sizeValue, variantSku, variantPrice) {
      this.selectedVariantId = variantId;
      this.selectedSizeValue = sizeValue;
      this.selectedVariantSku = variantSku;
      this.selectedVariantPrice = variantPrice;

      // Update display
      if (this.selectedSizeDisplay) {
        this.selectedSizeDisplay.textContent = sizeValue;
      }

      // Update header text for form step
      if (this.subheading) {
        this.subheading.textContent = "We'll notify you when it's back in stock";
      }

      // Hide sizes, show form
      this.sizesContainer?.setAttribute('hidden', '');
      this.formContainer?.removeAttribute('hidden');

      // Clear any previous errors
      this.errorMessage?.setAttribute('hidden', '');

      // Focus email input
      requestAnimationFrame(() => {
        this.emailInput?.focus();
      });

      // Pre-fill email if customer is logged in
      this.prefillCustomerEmail();
    }

    showSuccess() {
      this.form?.setAttribute('hidden', '');
      this.errorMessage?.setAttribute('hidden', '');
      this.successMessage?.removeAttribute('hidden');

      // Update subheading for success state
      if (this.subheading) {
        this.subheading.textContent = "We'll let you know when it's available";
      }

      // Auto-close after 3 seconds
      setTimeout(() => {
        this.close();
      }, 3000);
    }

    showError(message) {
      if (this.errorText) {
        this.errorText.textContent = message;
      }
      this.errorMessage?.removeAttribute('hidden');

      // Hide after delay
      setTimeout(() => {
        this.errorMessage?.setAttribute('hidden', '');
      }, 5000);
    }

    updateSizesForCurrentColor() {
      this.updateSizesForCurrentSelection();
    }

    updateSizesForCurrentSelection() {
      if (!this.sizesList) return;

      const sizeItems = this.sizesList.querySelectorAll('.bis-drawer__size-item');
      const activeLength = this.selectedLength || this.currentLength;

      sizeItems.forEach(item => {
        const sizeValue = item.dataset.sizeValue;

        // Find variant matching this size + current colour + selected length
        let matchingVariant = null;

        for (const variant of this.variants) {
          const variantSize = variant.options[this.sizeOptionPosition - 1];
          if (variantSize !== sizeValue) continue;

          // Check colour match
          if (this.colorOptionPosition && this.currentColor) {
            const variantColor = variant.options[this.colorOptionPosition - 1];
            if (variantColor !== this.currentColor) continue;
          }

          // Check length match
          if (this.lengthOptionPosition && activeLength) {
            const variantLength = variant.options[this.lengthOptionPosition - 1];
            if (variantLength !== activeLength) continue;
          }

          matchingVariant = variant;
          break;
        }

        if (matchingVariant) {
          const isAvailable = matchingVariant.available;

          // Update data attributes
          item.dataset.variantId = matchingVariant.id;
          item.dataset.available = String(isAvailable);
          item.dataset.variantSku = matchingVariant.sku || '';
          item.dataset.variantPrice = matchingVariant.price;

          // Update classes
          item.classList.toggle('bis-drawer__size-item--unavailable', !isAvailable);

          // Update button action
          const button = item.querySelector('.bis-drawer__size-button');
          if (button) {
            button.dataset.action = isAvailable ? 'select-size' : 'notify-me';
          }

          // Update status text
          const statusEl = item.querySelector('.bis-drawer__size-status');
          if (statusEl) {
            statusEl.textContent = isAvailable ? 'In Stock' : 'Notify Me';
          }

          // Show the item
          item.style.display = '';
        } else {
          // No variant for this combination - hide it
          item.style.display = 'none';
        }
      });
    }

    preselectCurrentSize() {
      // Get the currently selected size from the PDP variant picker
      const productInfo = document.querySelector('product-info');
      if (!productInfo) return;

      // Try to find the selected size input
      const sizeInputs = productInfo.querySelectorAll('input[type="radio"]:checked');
      let currentSize = null;

      for (const input of sizeInputs) {
        // Check if this is a size option by looking at the fieldset or parent
        const fieldset = input.closest('fieldset');
        const legend = fieldset?.querySelector('legend, .form__label');
        const legendText = legend?.textContent?.toLowerCase() || '';

        if (legendText.includes('size') || legendText.includes('taille')) {
          currentSize = input.value;
          break;
        }
      }

      // Also try to get size from the variant-radios data
      if (!currentSize) {
        const variantRadios = productInfo.querySelector('variant-radios, variant-selects');
        if (variantRadios) {
          const sizeFieldset = variantRadios.querySelector('[data-option-index="1"], fieldset:nth-child(2)');
          const checkedInput = sizeFieldset?.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            currentSize = checkedInput.value;
          }
        }
      }

      // Clear all selected states first
      const sizeItems = this.sizesList?.querySelectorAll('.bis-drawer__size-item');
      sizeItems?.forEach(item => {
        item.classList.remove('bis-drawer__size-item--selected');
      });

      // If we found a current size, select it
      if (currentSize && this.sizesList) {
        const matchingItem = this.sizesList.querySelector(`[data-size-value="${currentSize}"]`);
        if (matchingItem) {
          matchingItem.classList.add('bis-drawer__size-item--selected');
        }
      }
    }

    updateSelectedSize(sizeItem) {
      // Clear all selected states
      const sizeItems = this.sizesList?.querySelectorAll('.bis-drawer__size-item');
      sizeItems?.forEach(item => {
        item.classList.remove('bis-drawer__size-item--selected');
      });

      // Add selected to clicked item
      sizeItem.classList.add('bis-drawer__size-item--selected');
    }

    // ----- SWYM API INTEGRATION -----

    async submitToSwym(email) {
      this.isSubmitting = true;
      this.submitButton?.classList.add('loading');
      this.submitButton?.setAttribute('disabled', 'disabled');
      this.submitSpinner?.classList.remove('hidden');
      this.errorMessage?.setAttribute('hidden', '');

      try {
        // Ensure SWYM is loaded
        await this.ensureSwymLoaded();

        if (!window._swat) {
          throw new Error('To receive stock notifications, please accept cookies via the cookie banner and try again.');
        }

        // Get variant details
        const variant = this.variants.find(v => v.id === parseInt(this.selectedVariantId));
        const price = variant?.price ? (variant.price / 100) : 0;

        // SWYM sendWatchlist API call with timeout
        // Ref: https://developers.getswym.com/docs/request-a-back-in-stock-alert
        const swymProduct = {
          epi: parseInt(this.selectedVariantId),
          empi: parseInt(this.productId),
          du: window.location.origin + this.productUrl,
          iu: this.productImage,
          pr: price
        };

        const swymPromise = new Promise((resolve, reject) => {
          // Method 1: sendWatchlist (official documented API)
          if (typeof window._swat.sendWatchlist === 'function') {
            window._swat.sendWatchlist(email, 'email', swymProduct, resolve, reject);
          }
          // Method 2: fetch wrapper (fire-and-forget fallback)
          else if (typeof window._swat.fetch === 'function') {
            window._swat.fetch('addToWaitlist', { ...swymProduct, email });
            setTimeout(() => resolve({ status: 'submitted' }), 1500);
          }
          else {
            reject(new Error('SWYM API method not available'));
          }
        });

        // Timeout: reject if SWYM doesn't respond within 10 seconds
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timed out. Please try again.')), 10000);
        });

        const result = await Promise.race([swymPromise, timeoutPromise]);
        this.showSuccess();

        // Store email for future pre-fill
        try {
          localStorage.setItem('bis_customer_email', email);
        } catch (e) {
          // localStorage not available
        }

        // Fire custom event for analytics
        this.dispatchEvent(new CustomEvent('bis-drawer:subscribed', {
          bubbles: true,
          detail: {
            email,
            variantId: this.selectedVariantId,
            productId: this.productId,
            size: this.selectedSizeValue
          }
        }));

      } catch (error) {
        console.error('[BIS Drawer] Subscription error:', error);
        this.showError(error.message || 'Something went wrong. Please try again.');
      } finally {
        this.isSubmitting = false;
        this.submitButton?.classList.remove('loading');
        this.submitButton?.removeAttribute('disabled');
        this.submitSpinner?.classList.add('hidden');
      }
    }

    ensureSwymLoaded() {
      return new Promise((resolve) => {
        if (window._swat) {
          resolve();
          return;
        }

        // Queue callback for when SWYM loads
        window.SwymCallbacks = window.SwymCallbacks || [];
        window.SwymCallbacks.push(() => {
          resolve();
        });

        // Timeout fallback
        setTimeout(() => {
          resolve();
        }, 5000);
      });
    }

    // ----- HELPER METHODS -----

    selectAvailableVariant(variantId) {
      // Close drawer
      this.close();

      // Dispatch event for product-info to handle
      this.dispatchEvent(new CustomEvent('bis-drawer:variant-selected', {
        bubbles: true,
        detail: { variantId }
      }));

      // Try to find and click the variant option on the page
      const productInfo = document.querySelector('product-info');
      if (productInfo) {
        // Find the variant in our list
        const variant = this.variants.find(v => v.id === parseInt(variantId));
        if (variant && variant.options) {
          // Try to find and click the size option
          const sizeValue = variant.options[this.sizeOptionPosition - 1];
          if (sizeValue) {
            const sizeInput = productInfo.querySelector(
              `input[type="radio"][value="${sizeValue}"], ` +
              `input[data-option-value-id][value="${sizeValue}"]`
            );
            if (sizeInput) {
              sizeInput.click();
              return;
            }
          }
        }
      }
    }

    prefillCustomerEmail() {
      // Try to get customer email from various sources
      let customerEmail = null;

      // Check Shopify customer
      if (window.ShopifyAnalytics?.meta?.page?.customerId) {
        // Customer is logged in but we don't have email directly
        // Check for email in forms
        const customerEmailInput = document.querySelector('input[name="customer[email]"]');
        if (customerEmailInput?.value) {
          customerEmail = customerEmailInput.value;
        }
      }

      // Check localStorage
      if (!customerEmail) {
        try {
          customerEmail = localStorage.getItem('bis_customer_email');
        } catch (e) {
          // localStorage not available
        }
      }

      if (customerEmail && this.emailInput && !this.emailInput.value) {
        this.emailInput.value = customerEmail;
      }
    }

    validateEmail(email) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    }
  }

  customElements.define('back-in-stock-drawer', BackInStockDrawer);
}

// Global function to open BIS drawer (for use in onclick handlers)
window.openBackInStockDrawer = function(productId, triggeredBy) {
  // Try to find drawer - first by product ID, then any drawer
  let drawer = document.querySelector(`back-in-stock-drawer[data-product-id="${productId}"]`);

  if (!drawer) {
    drawer = document.querySelector('back-in-stock-drawer');
  }

  if (drawer) {
    drawer.open(triggeredBy);
  } else {
    console.error('[BIS Drawer] No drawer found on page.');
  }
};
